import { useAuth } from "@dl/web-core-js/auth";
import { useExport } from "@dl/web-core-js/shell";
import { Badge, Button, Card, cn, Switch, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ProductDTO } from "../../api/product";
import { ProductLink } from "../../components/ProductLink";
import {
  ShopifyClient,
  type ShopifyListingPublication,
  type ShopifyListingVariant,
  type ShopifyPreviewDTO,
  type ShopifyPublicationTarget,
} from "../../api/shopify";

/**
 * The product's group on Shopify, read live: nexpim keeps only the product gid
 * and the two opt-outs, so everything on this tab — status, publications,
 * variants, media, tags — is what Shopify holds at the moment the tab opens,
 * and the preview beside it is what a sync would change. The actions here are
 * per group: a product page is the door, the variation group is the listing.
 */
export function ShopifyTab({ draft, readOnly }: { draft: ProductDTO; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new ShopifyClient(http), [http]);
  const { start: startJob, busy } = useExport();

  const query = useQuery({
    queryKey: ["shopify", "listing", draft.id],
    queryFn: () => client.view(draft.id),
    enabled: draft.id > 0,
    staleTime: 60_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["shopify", "listing", draft.id] });

  /** The button's refetch, with feedback: the read takes a second or two and nothing moves otherwise. */
  const reload = async () => {
    const result = await query.refetch();
    if (result.error) {
      toast.error(result.error instanceof Error ? result.error.message : t("shopify.tab.unavailable", "Shopify could not be read."));
    } else {
      toast.success(t("shopify.refreshed", "Shopify data reloaded"));
    }
  };

  const run = async (label: string, work: () => Promise<unknown>) => {
    try {
      await work();
      await refresh();
      toast.success(label);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Request failed"));
    }
  };

  const view = query.data;

  if (draft.id <= 0) {
    return <p className="text-[13px] text-ink-faint">{t("shopify.tab.unsaved", "Save the product first.")}</p>;
  }
  if (query.isLoading) {
    return <p className="text-[13px] text-ink-faint">{t("loading", "Loading…")}</p>;
  }
  if (query.error || !view) {
    return (
      <div className="space-y-2">
        <p className="text-[13px] text-risk">
          {query.error instanceof Error ? query.error.message : t("shopify.tab.unavailable", "Shopify could not be read.")}
        </p>
        <Button size="sm" onClick={() => void reload()} disabled={query.isFetching} icon={<RefreshCw className={cn("size-4", query.isFetching && "animate-spin")} />}>
          {t("button.retry", "Retry")}
        </Button>
      </div>
    );
  }

  const preview = view.preview;
  const managed = view.publications.filter((p) => p.target);
  const others = view.publications.filter((p) => !p.target && p.published);

  return (
    <div className="flex flex-col gap-4">
      <StatusCard
        view={view}
        readOnly={readOnly}
        busy={busy !== null}
        onSync={() =>
          void startJob("shopify-sync-" + draft.id, () => client.sync([draft.id]), t("shopify.sync.failed", "Sync failed"))
        }
        onRefresh={() => void reload()}
        refreshing={query.isFetching}
        onArchive={async () => {
          if (
            await confirm({
              title: t("shopify.archive.title", "Archive on Shopify?"),
              message: t("shopify.archive.message", "The product goes off every managed publication and its status becomes ARCHIVED. Orders and reviews stay."),
              variant: "danger",
              confirmLabel: t("shopify.archive.confirm", "Archive"),
            })
          ) {
            await run(t("shopify.archived", "Archived on Shopify"), () => client.archive([draft.id]));
          }
        }}
        onDelete={async () => {
          if (
            await confirm({
              title: t("shopify.delete.title", "Delete from Shopify?"),
              message: t("shopify.delete.message", "The Shopify product is deleted for good; nexpim forgets it and a later sync creates a new one."),
              consequences: [
                t("shopify.delete.consequence.reviews", "Its reviews and its place in orders' history are lost."),
                t("shopify.delete.consequence.url", "Its URL stops working."),
              ],
              variant: "danger",
              requireText: "DELETE",
            })
          ) {
            await run(t("shopify.deleted", "Deleted from Shopify"), () => client.remove([draft.id]));
          }
        }}
        onUnlink={async () => {
          if (
            await confirm({
              title: t("shopify.unlink.title", "Forget the Shopify product?"),
              message: t("shopify.unlink.message", "Only for a product deleted in the Shopify admin. Nexpim drops the stored id; the next sync creates a new product."),
              consequences: [
                t("shopify.unlink.consequence.orphan", "If the product still exists on Shopify it is left there, orphaned, and the next sync creates a duplicate."),
                t("shopify.unlink.consequence.undo", "There is no undo: the id can only be recovered from the Shopify admin."),
              ],
              variant: "danger",
              // Deliberately not translated: a literal the user must reproduce.
              requireText: "FORGET",
            })
          ) {
            await run(t("shopify.unlinked", "Link removed"), () => client.unlink(draft.id));
          }
        }}
        onGroupExcluded={(excluded) =>
          void run(
            excluded ? t("shopify.group.excluded", "Group excluded from Shopify") : t("shopify.group.included", "Group included"),
            () => client.setGroupExcluded(draft.id, excluded),
          )
        }
      />

      {view.listed && (
        <Card className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("shopify.publications", "Publications")}</h3>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            {managed.map((p) => (
              <PublicationSwitch
                key={p.id}
                publication={p}
                readOnly={readOnly}
                onChange={(published) =>
                  void run(
                    published ? t("shopify.published", "Published") : t("shopify.unpublished", "Unpublished"),
                    () => client.setPublished([draft.id], p.target as ShopifyPublicationTarget, published),
                  )
                }
              />
            ))}
          </div>
          <p className="mt-2 text-[12px] text-ink-faint">
            {t("shopify.publications.gate", "The Online Store makes the product visible on the website; the market switches decide which buyers see it.")}
          </p>
          {others.length > 0 && (
            <p className="mt-2 text-[12px] text-ink-faint">
              {t("shopify.publications.others", "Also on: {{names}}", { names: others.map((p) => p.name).join(", ") })}
            </p>
          )}
        </Card>
      )}

      <PreviewCard preview={preview} />

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-5 pb-3">
          <h3 className="text-[13px] font-semibold text-ink">{t("shopify.variants", "Variants")}</h3>
          <p className="text-[12px] text-ink-faint">
            {t(
              "shopify.default.hint",
              "Default: the variant the product page opens on, its images first. When it is sold out the page opens on the next one in SKU order. Applied on the next sync.",
            )}
          </p>
        </div>
        <VariantsTable
          variants={view.variants}
          readOnly={readOnly}
          currentProductId={draft.id}
          defaultProductId={view.default_product_id ?? null}
          onDefault={(productId) =>
            void run(
              productId == null ? t("shopify.default.cleared", "Back to SKU order") : t("shopify.default.set", "Default variant set"),
              () => client.setDefaultProduct(draft.id, productId),
            )
          }
          onExcluded={(variant, excluded) =>
            void run(
              excluded ? t("shopify.variant.excluded", "Left out of the listing") : t("shopify.variant.included", "Back in the listing"),
              () => client.setProductExcluded(variant.product_id, excluded),
            )
          }
        />
      </Card>

      {view.media.length > 0 && (
        <Card className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("shopify.media", "Media on Shopify")}</h3>
          <div className="flex flex-wrap gap-2">
            {view.media.map((m) => (
              <figure key={m.id} className="w-28">
                {m.url ? (
                  <img src={m.url} alt={m.alt ?? ""} className="w-28 h-28 object-contain border border-line rounded" loading="lazy" />
                ) : (
                  <div className="w-28 h-28 border border-line rounded flex items-center justify-center text-[11px] text-ink-faint">{m.status}</div>
                )}
                <figcaption className="text-[11px] text-ink-faint truncate" title={m.alt ?? ""}>
                  {m.alt || t("shopify.media.no-alt", "(no alt text)")}
                </figcaption>
              </figure>
            ))}
          </div>
        </Card>
      )}

      {view.tags.length > 0 && (
        <Card className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("shopify.tags", "Tags")}</h3>
          <div className="flex flex-wrap gap-1">
            {view.tags.map((tag) => (
              <Badge key={tag} size="sm" variant="neutral">
                {tag}
              </Badge>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function StatusCard({
  view,
  readOnly,
  busy,
  onSync,
  onRefresh,
  refreshing,
  onArchive,
  onDelete,
  onUnlink,
  onGroupExcluded,
}: {
  view: import("../../api/shopify").ShopifyListingViewDTO;
  readOnly: boolean;
  busy: boolean;
  onSync: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  onArchive: () => void;
  onDelete: () => void;
  onUnlink: () => void;
  onGroupExcluded: (excluded: boolean) => void;
}) {
  const { t } = useTranslation();
  const statusVariant = view.status === "ACTIVE" ? "success" : view.status === "ARCHIVED" ? "neutral" : "warning";
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold">{view.title}</span>
            {view.listed ? (
              <Badge variant={statusVariant} size="sm">
                {view.status ?? "?"}
              </Badge>
            ) : (
              <Badge variant="neutral" size="sm">
                {t("shopify.status.not-listed", "Not on Shopify")}
              </Badge>
            )}
            {view.excluded && (
              <Badge variant="warning" size="sm">
                {t("shopify.status.excluded", "Excluded")}
              </Badge>
            )}
            {view.has_french && (
              <Badge variant="neutral" size="sm">
                FR
              </Badge>
            )}
          </div>
          <p className="text-[12px] text-ink-faint">
            {t("shopify.group", "Variation group")} {view.variation_group}
            {view.handle ? ` · /${view.handle}` : ""}
            {view.product_gid ? ` · ${view.product_gid}` : ""}
          </p>
          <div className="flex gap-3 text-[12px]">
            {view.admin_url && (
              <a href={view.admin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
                {t("shopify.link.admin", "Shopify admin")} <ExternalLink className="size-3" />
              </a>
            )}
            {Object.keys(view.storefront_urls ?? {}).length > 0
              ? Object.entries(view.storefront_urls).map(([market, url]) => (
                  <a key={market} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
                    {t("shopify.link.storefront", "Storefront")} {market.toUpperCase()} <ExternalLink className="size-3" />
                  </a>
                ))
              : view.storefront_url && (
                  <a href={view.storefront_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
                    {t("shopify.link.storefront", "Storefront")} <ExternalLink className="size-3" />
                  </a>
                )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" onClick={onRefresh} disabled={refreshing} icon={<RefreshCw className={cn("size-4", refreshing && "animate-spin")} />}>
            {refreshing ? t("shopify.refreshing", "Reloading…") : t("button.refresh", "Refresh")}
          </Button>
          {!readOnly && (
            <>
              <Button size="sm" variant="primary" disabled={busy || view.excluded || view.preview.blocked} onClick={onSync}>
                {view.listed ? t("shopify.action.update", "Update on Shopify") : t("shopify.action.create", "Create on Shopify")}
              </Button>
              {view.listed && (
                <>
                  <Button size="sm" onClick={onArchive}>
                    {t("shopify.action.archive", "Archive")}
                  </Button>
                  <Button size="sm" variant="danger" onClick={onDelete}>
                    {t("shopify.action.delete", "Delete")}
                  </Button>
                  <Button size="sm" onClick={onUnlink}>
                    {t("shopify.action.unlink", "Forget link")}
                  </Button>
                </>
              )}
              <Switch
                checked={view.excluded}
                onChange={onGroupExcluded}
                label={t("shopify.group.exclude", "Never sync this group to Shopify")}
              />
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function PublicationSwitch({
  publication,
  readOnly,
  onChange,
}: {
  publication: ShopifyListingPublication;
  readOnly: boolean;
  onChange: (published: boolean) => void;
}) {
  const { t } = useTranslation();
  const label =
    publication.target === "ONLINE_STORE"
      ? t("shopify.publication.online-store", "Online Store")
      : publication.target === "CA"
        ? t("shopify.publication.ca", "Canada market")
        : t("shopify.publication.us", "United States market");
  return <Switch checked={publication.published} onChange={onChange} disabled={readOnly} label={label} />;
}

function PreviewCard({ preview }: { preview: ShopifyPreviewDTO }) {
  const { t } = useTranslation();
  const blocking = preview.problems.filter((p) => p.blocking);
  const warnings = preview.problems.filter((p) => !p.blocking);
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-[13px] font-semibold">{t("shopify.preview", "Next sync")}</h3>
        {preview.blocked ? (
          <Badge variant="error" size="sm">
            {t("shopify.preview.blocked", "Blocked")}
          </Badge>
        ) : preview.noop ? (
          <Badge variant="success" size="sm">
            {t("shopify.preview.noop", "Nothing to change")}
          </Badge>
        ) : preview.create ? (
          <Badge variant="warning" size="sm">
            {t("shopify.preview.create", "Would create")}
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            {t("shopify.preview.update", "Would update")}
          </Badge>
        )}
      </div>
      {blocking.length > 0 && (
        <ul className="text-[12px] text-risk list-disc pl-5 mb-2">
          {blocking.map((p, i) => (
            <li key={i}>
              {p.sku ? `${p.sku} · ` : ""}
              {p.code}: {p.message}
            </li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="text-[12px] text-ink-faint list-disc pl-5 mb-2">
          {warnings.map((p, i) => (
            <li key={i}>
              {p.sku ? `${p.sku} · ` : ""}
              {p.message}
            </li>
          ))}
        </ul>
      )}
      {!preview.blocked && !preview.noop && (
        <div className="text-[12px] space-y-1">
          {preview.changes.length > 0 && (
            <details>
              <summary className="cursor-pointer">{t("shopify.preview.changes", "{{count}} change(s)", { count: preview.changes.length })}</summary>
              <ul className="list-disc pl-5 mt-1 font-mono text-[11px] break-all">
                {preview.changes.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </details>
          )}
          {preview.tags_add.length > 0 && (
            <p>
              {t("shopify.preview.tags-add", "Tags to add")}: {preview.tags_add.join(", ")}
            </p>
          )}
          {preview.tags_remove.length > 0 && (
            <p>
              {t("shopify.preview.tags-remove", "Tags to remove")}: {preview.tags_remove.join(", ")}
            </p>
          )}
          {preview.translations && <p>{t("shopify.preview.translations", "French title and description will be registered")}</p>}
        </div>
      )}
    </Card>
  );
}

function VariantsTable({
  variants,
  readOnly,
  currentProductId,
  defaultProductId,
  onDefault,
  onExcluded,
}: {
  variants: ShopifyListingVariant[];
  readOnly: boolean;
  currentProductId: number;
  defaultProductId: number | null;
  onDefault: (productId: number | null) => void;
  onExcluded: (variant: ShopifyListingVariant, excluded: boolean) => void;
}) {
  const { t } = useTranslation();
  if (variants.length === 0) {
    return <p className="px-5 pb-5 text-[13px] text-ink-faint">{t("shopify.variants.empty", "No sellable product in this group")}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="text-left text-ink-faint">
          <tr className="border-t border-line">
            <th className="px-5 py-2 font-medium" />
            <th className="px-3 py-2 font-medium">
              <span className="inline-flex items-center gap-2">
                {t("shopify.column.default", "Default")}
                {defaultProductId != null && !readOnly && (
                  <button type="button" className="text-[11px] font-normal text-accent hover:underline" onClick={() => onDefault(null)}>
                    {t("shopify.default.clear", "SKU order")}
                  </button>
                )}
              </span>
            </th>
            <th className="px-3 py-2 font-medium">{t("column.sku", "SKU")}</th>
            <th className="px-3 py-2 font-medium">{t("shopify.column.options", "Options")}</th>
            <th className="px-3 py-2 font-medium">{t("column.status", "Status")}</th>
            <th className="px-3 py-2 font-medium text-right">{t("column.price", "Price")}</th>
            <th className="px-3 py-2 font-medium">{t("shopify.column.on-shopify", "Shopify")}</th>
            <th className="px-3 py-2 font-medium">{t("shopify.column.excluded", "Excluded")}</th>
            <th className="px-5 py-2 font-medium">{t("shopify.column.problem", "Problem")}</th>
          </tr>
        </thead>
        <tbody>
          {variants.map((row) => (
            <tr key={row.product_id} className={cn("border-t border-line", row.product_id === currentProductId && "bg-surface-sunk")}>
              <td className="px-5 py-2">
                {row.image_url ? <img src={row.image_url} alt="" className="h-10 w-10 object-contain" loading="lazy" /> : null}
              </td>
              <td className="px-3 py-2">
                <input
                  type="radio"
                  name="shopify-default-variant"
                  className="size-3.5 accent-[var(--accent)]"
                  checked={row.product_id === defaultProductId}
                  disabled={readOnly || row.excluded}
                  aria-label={t("shopify.column.default", "Default")}
                  onChange={() => onDefault(row.product_id)}
                />
              </td>
              <td className={cn("px-3 py-2 tabular", row.product_id === currentProductId && "font-semibold")}>
                {row.product_id === currentProductId ? row.sku : <ProductLink id={row.product_id}>{row.sku}</ProductLink>}
              </td>
              <td className="px-3 py-2">{row.option_values}</td>
              <td className="px-3 py-2">{row.status ?? ""}</td>
              <td className="px-3 py-2 text-right tabular">
                {row.price == null ? <span className="text-risk">{t("shopify.no-price", "no contract price")}</span> : row.price.toFixed(2)}
              </td>
              <td className="px-3 py-2">
                {row.variant_gid ? (
                  <Badge variant="success" size="sm">{t("shopify.variant.on-shopify", "On Shopify")}</Badge>
                ) : (
                  <Badge variant="neutral" size="sm">{t("shopify.variant.not-on-shopify", "Not yet")}</Badge>
                )}
              </td>
              <td className="px-3 py-2">
                <Switch checked={row.excluded} disabled={readOnly} onChange={(checked) => onExcluded(row, checked)} label="" />
              </td>
              <td className="px-5 py-2 text-risk">{row.problem ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
